/**
 * Level Select Scene - Enhanced
 *
 * Features:
 * - Hero teaser image with tilt parallax effect
 * - Medal targets display
 * - Best times leaderboard
 * - Victory statistics
 * - Clean dark background with particles
 */

import Phaser from 'phaser';
import { VictoryHistoryManager } from '../systems/VictoryHistoryManager';
import { GlassButton } from '../ui/components/GlassButton';
import { MenuParticleSystem } from '../ui/effects/MenuParticleSystem';
import { TYPOGRAPHY, Z_INDEX } from '../config/designSystem';
import { ICONS } from '../config/icons';

export type LevelType = 'test' | 'main';

export class LevelSelectScene extends Phaser.Scene {
  private tiltAngle: number = 0;
  private baselineTiltAngle: number | null = null; // Baseline for tilt calibration
  private teaserImage?: Phaser.GameObjects.Image;

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  preload(): void {
    // Load level teaser image
    this.load.image('level1_teaser', 'bg/level1_teaser.jpg');
  }

  create(): void {
    const centerX = this.cameras.main.centerX;
    const screenWidth = this.cameras.main.width;
    const screenHeight = this.cameras.main.height;

    // Reset tilt angle to 0 when scene opens
    this.tiltAngle = 0;
    this.baselineTiltAngle = null; // Reset baseline for recalibration

    // Dark background matching main menu
    this.cameras.main.setBackgroundColor('#0A0E27');

    // Add floating particles
    new MenuParticleSystem(this);

    // Set up tilt system for parallax effects
    this.setupTiltSystem();

    // Hero teaser image at top (scaled 130% and positioned higher to extend to top and prevent edges)
    const teaserY = 100; // Moved up from 150 to extend to top
    const teaserWidth = Math.min(screenWidth * 0.95, 720);
    const teaserHeight = teaserWidth * (9 / 16); // 16:9 aspect ratio

    this.teaserImage = this.add.image(centerX, teaserY, 'level1_teaser');
    this.teaserImage.setDisplaySize(teaserWidth * 1.3, teaserHeight * 1.3); // Scale 130% (was 125%)
    this.teaserImage.setDepth(Z_INDEX.background);

    // Main Level Card (no background) - spacing below larger header
    const cardY = teaserY + (teaserHeight * 1.3) / 2 + 100; // 100px spacing from header
    this.createLevelCard(
      centerX,
      cardY,
      [
        'Begin at the forest floor and climb',
        'through mysterious zones toward',
        'the unknown heights above.',
      ],
      () => this.startLevel('main')
    );

    // Add stats row with icons separately (below the card description)
    const statsY = cardY - 200 / 2 + 30 + 24 * 3 + 24; // Position after 3 text lines + 1 empty line
    this.createStatsRow(centerX, statsY);

    // Best Times Section - static position (150px below card)
    const bestTimesY = cardY + 150; // Fixed position - never changes
    this.displayMedalTargetsAndBestTimes(centerX, bestTimesY);

    // Back button
    const backButton = new GlassButton(this, centerX, screenHeight - 50, {
      text: '← BACK TO MENU',
      fontSize: 20,
      width: 220,
      height: 52,
      style: 'secondary',
      onClick: () => this.scene.start('MainMenuScene'),
    });
    backButton.setDepth(Z_INDEX.modal);
    backButton.setScrollFactor(0); // Keep button fixed on screen

    // Enable camera scrolling
    const totalContentHeight = bestTimesY + 400; // Approximate total height
    this.cameras.main.setBounds(0, 0, screenWidth, Math.max(screenHeight, totalContentHeight));
    this.cameras.main.setScroll(0, 0);

    // Enable mouse wheel scrolling
    this.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _gameObjects: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number
      ) => {
        const camera = this.cameras.main;
        const newScrollY = Phaser.Math.Clamp(
          camera.scrollY + deltaY * 0.5,
          0,
          Math.max(0, totalContentHeight - screenHeight)
        );
        camera.setScroll(camera.scrollX, newScrollY);
      }
    );

    // Enable touch scrolling
    let isDragging = false;
    let dragStartY = 0;
    let scrollStartY = 0;

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      isDragging = true;
      dragStartY = pointer.y;
      scrollStartY = this.cameras.main.scrollY;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (isDragging) {
        const dragDistance = dragStartY - pointer.y;
        const camera = this.cameras.main;
        const newScrollY = Phaser.Math.Clamp(
          scrollStartY + dragDistance,
          0,
          Math.max(0, totalContentHeight - screenHeight)
        );
        camera.setScroll(camera.scrollX, newScrollY);
      }
    });

    this.input.on('pointerup', () => {
      isDragging = false;
    });
  }

  update(): void {
    // Update parallax positions based on tilt (only teaser image)
    if (this.teaserImage) {
      // Extended parallax movement range (up to 60px at ±30° tilt for smooth, noticeable effect)
      const teaserOffset = this.tiltAngle * 2.0;
      this.teaserImage.x = this.cameras.main.centerX + teaserOffset;
    }
  }

  /**
   * Set up tilt system for parallax effects
   */
  private setupTiltSystem(): void {
    // Listen for device orientation
    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', this.handleDeviceOrientation.bind(this));
    }

    // Keyboard simulation for testing (wider range)
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        this.tiltAngle = Math.max(this.tiltAngle - 3, -30);
        this.emitTiltUpdate();
      } else if (event.key === 'ArrowRight') {
        this.tiltAngle = Math.min(this.tiltAngle + 3, 30);
        this.emitTiltUpdate();
      }
    });
  }

  /**
   * Handle device orientation events
   */
  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    if (event.gamma !== null) {
      // Set baseline on first orientation event (centers image regardless of device tilt)
      if (this.baselineTiltAngle === null) {
        this.baselineTiltAngle = event.gamma;
      }

      // Calculate relative tilt from baseline
      const relativeTilt = event.gamma - this.baselineTiltAngle;

      // Clamp to ±30 degrees for noticeable, smooth effect
      this.tiltAngle = Phaser.Math.Clamp(relativeTilt, -30, 30);
      this.emitTiltUpdate();
    }
  }

  /**
   * Emit tilt update event for particles
   */
  private emitTiltUpdate(): void {
    this.events.emit('tilt-update', this.tiltAngle);
  }

  /**
   * Create level card with features and play button
   */
  private createLevelCard(x: number, y: number, features: string[], onClick: () => void): void {
    const cardHeight = 200;

    // Features list (no background, just text)
    let featureY = y - cardHeight / 2 + 30;
    features.forEach(feature => {
      const featureText = this.add.text(x, featureY, feature, {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: '17px',
        color: '#FFFFFF',
        align: 'center',
        resolution: Math.max(2, window.devicePixelRatio) * 2,
      });
      featureText.setOrigin(0.5);
      featureText.setDepth(Z_INDEX.uiContent);
      featureY += 24;
    });

    // Add spacing line before button
    const buttonDivider = this.add.rectangle(x, y + cardHeight / 2 - 50, 200, 1, 0xffffff, 0.3);
    buttonDivider.setDepth(Z_INDEX.uiContent);

    // Play button (with extra spacing above) - same size as main menu
    const playButton = new GlassButton(this, x, y + cardHeight / 2 - 20, {
      text: 'PLAY',
      iconKey: ICONS.PLAY_CIRCLE,
      fontSize: 28,
      width: 260,
      height: 64,
      style: 'primary',
      onClick: onClick,
    });
    playButton.setDepth(Z_INDEX.uiContent);
  }

  /**
   * Display medal targets and best times
   */
  private displayMedalTargetsAndBestTimes(x: number, startY: number): void {
    const runs = VictoryHistoryManager.loadRuns();

    // Title with trophy icon
    const titleY = startY + 15;
    const title = this.add.text(x, titleY, 'BEST TIMES', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '24px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    title.setOrigin(0.5);
    title.setDepth(Z_INDEX.uiContent);

    // Trophy icon to the left of title
    const iconSize = 24;
    const spacing = 10;
    const iconX = x - title.width / 2 - iconSize / 2 - spacing;
    const trophyIcon = this.add.image(iconX, titleY, ICONS.TROPHY);
    trophyIcon.setDisplaySize(iconSize, iconSize);
    trophyIcon.setTint(0xffd700); // Gold tint
    trophyIcon.setDepth(Z_INDEX.uiContent);

    // Medal targets section - static layout
    let currentY = startY + 45;

    // Helper to create medal icon + text
    const createMedalRow = (y: number, text: string, color: number, medalIconKey: string): void => {
      const medalText = this.add.text(x, y, text, {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: '19px',
        color: `#${color.toString(16).padStart(6, '0')}`,
        resolution: Math.max(2, window.devicePixelRatio) * 2,
      });
      medalText.setOrigin(0.5);
      medalText.setDepth(Z_INDEX.uiContent);

      // Medal icon to the left (using colored SVG)
      const medalIconSize = 20;
      const medalSpacing = 8;
      const medalIconX = x - medalText.width / 2 - medalIconSize / 2 - medalSpacing;
      const medalIcon = this.add.image(medalIconX, y, medalIconKey);
      medalIcon.setDisplaySize(medalIconSize, medalIconSize);
      medalIcon.setOrigin(0.5);
      medalIcon.setDepth(Z_INDEX.uiContent);
    };

    // Gold target
    createMedalRow(currentY, 'Gold: < 5:00', 0xffd700, ICONS.MEDAL_GOLD);
    currentY += 28;

    // Silver target
    createMedalRow(currentY, 'Silver: 5:00-8:00', 0xe0e0e0, ICONS.MEDAL_SILVER);
    currentY += 28;

    // Bronze target
    createMedalRow(currentY, 'Bronze: 8:00-15:00', 0xe8a87c, ICONS.MEDAL_BRONZE);
    currentY += 40;

    // Best times section
    if (runs.length === 0) {
      const noDataText = this.add.text(x, currentY, 'No victories yet - start climbing!', {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: '17px',
        color: '#FFFFFF',
        align: 'center',
        resolution: Math.max(2, window.devicePixelRatio) * 2,
      });
      noDataText.setOrigin(0.5);
      noDataText.setDepth(Z_INDEX.uiContent);
      return;
    }

    // Divider (subtle white line)
    const divider = this.add.rectangle(x, currentY, 300, 2, 0xffffff, 0.3);
    divider.setDepth(Z_INDEX.uiContent);
    currentY += 15;

    // "TOP 5 RUNS" label
    const topRunsLabel = this.add.text(x, currentY, '— TOP 5 RUNS —', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '18px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    topRunsLabel.setOrigin(0.5);
    topRunsLabel.setDepth(Z_INDEX.uiContent);
    currentY += 28;

    // Show top 5 runs
    const topRuns = runs.slice(0, 5);
    topRuns.forEach((run, index) => {
      const time = VictoryHistoryManager.formatTime(run.completionTime);
      const date = new Date(run.timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });

      // Rank color gradient
      let rankColor = '#FFC107'; // Bright yellow for 4th-5th
      if (index === 0) {
        rankColor = '#FFD700'; // Brightest gold for 1st
      } else if (index === 1) {
        rankColor = '#FFEB3B'; // Bright yellow for 2nd
      } else if (index === 2) {
        rankColor = '#FFD54F'; // Medium yellow for 3rd
      }

      const runText = this.add.text(
        x,
        currentY,
        `${index + 1}.       ${time}  •  ${run.collectibles}/45  -  ${date}`,
        {
          fontFamily: TYPOGRAPHY.fonts.josefin,
          fontSize: '17px',
          color: rankColor,
          resolution: Math.max(2, window.devicePixelRatio) * 2,
        }
      );
      runText.setOrigin(0.5);
      runText.setDepth(Z_INDEX.uiContent);

      // Add medal icon after rank number (using colored SVG)
      const medalSize = 16;
      const textWidth = runText.width;
      const beforeMedal = `${index + 1}.  `;
      const medalX = x - textWidth / 2 + beforeMedal.length * 9.5; // Approximate character width

      // Medal icons based on type
      const medalIcons: Record<string, string> = {
        gold: ICONS.MEDAL_GOLD,
        silver: ICONS.MEDAL_SILVER,
        bronze: ICONS.MEDAL_BRONZE,
      };
      const medalIconKey = medalIcons[run.medal] || ICONS.MEDAL_GOLD;

      const medalIcon = this.add.image(medalX, currentY, medalIconKey);
      medalIcon.setDisplaySize(medalSize, medalSize);
      medalIcon.setOrigin(0.5);
      medalIcon.setDepth(Z_INDEX.uiContent);

      // Add diamond icon before collectibles count
      const diamondSize = 14;
      // Calculate position: find the "•" symbol and add icon after it
      const beforeCollectibles = `${index + 1}.       ${time}  •  `;
      const diamondX = x - textWidth / 2 + beforeCollectibles.length * 9; // Approximate character width
      const diamondIcon = this.add.image(diamondX, currentY, ICONS.DIAMOND);
      diamondIcon.setDisplaySize(diamondSize, diamondSize);
      diamondIcon.setTint(parseInt(rankColor.substring(1), 16));
      diamondIcon.setDepth(Z_INDEX.uiContent);

      // Pulse animation for first place
      if (index === 0) {
        this.tweens.add({
          targets: runText,
          alpha: { from: 0.8, to: 1 },
          duration: 1500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }

      currentY += 26;
    });
  }

  /**
   * Start level with orientation permission handling
   */
  private startLevel(levelType: LevelType): void {
    // Request device orientation permission if needed (iOS 13+)
    const needsPermission = this.registry.get('needsOrientationPermission') as boolean;

    if (needsPermission) {
      this.requestOrientationPermission()
        .then(() => {
          this.launchGame(levelType);
        })
        .catch(error => {
          console.error('Permission denied:', error);
          // Launch game anyway, tilt controls will be disabled
          this.launchGame(levelType);
        });
    } else {
      this.launchGame(levelType);
    }
  }

  /**
   * Create stats row with height and collectibles icons
   */
  private createStatsRow(x: number, y: number): void {
    // Create container for stats
    const heightIconSize = 18;
    const diamondIconSize = 16;
    const spacing = 8;

    // Height section
    const heightText = this.add.text(0, 0, 'Height: 130m', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '17px',
      color: '#FFFFFF',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    heightText.setOrigin(0.5);

    const heightIcon = this.add.image(0, 0, ICONS.HEIGHT);
    heightIcon.setDisplaySize(heightIconSize, heightIconSize);
    heightIcon.setTint(0xffffff);

    // Collectibles section
    const collectiblesText = this.add.text(0, 0, '45 Collectibles', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '17px',
      color: '#FFFFFF',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    collectiblesText.setOrigin(0.5);

    const diamondIcon = this.add.image(0, 0, ICONS.DIAMOND);
    diamondIcon.setDisplaySize(diamondIconSize, diamondIconSize);
    diamondIcon.setTint(0xffffff);

    // Layout: [icon] Height: 130m  •  [icon] 45 Collectibles
    const separatorText = '  •  ';
    const totalWidth =
      heightIconSize +
      spacing +
      heightText.width +
      spacing +
      separatorText.length * 8 +
      spacing +
      diamondIconSize +
      spacing +
      collectiblesText.width;

    let currentX = x - totalWidth / 2;

    // Position height icon
    heightIcon.setPosition(currentX + heightIconSize / 2, y);
    currentX += heightIconSize + spacing;

    // Position height text
    heightText.setPosition(currentX + heightText.width / 2, y);
    currentX += heightText.width + spacing;

    // Add separator
    const separator = this.add.text(currentX, y, separatorText, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '17px',
      color: '#FFFFFF',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    separator.setOrigin(0, 0.5);
    currentX += separatorText.length * 8 + spacing;

    // Position diamond icon
    diamondIcon.setPosition(currentX + diamondIconSize / 2, y);
    currentX += diamondIconSize + spacing;

    // Position collectibles text
    collectiblesText.setPosition(currentX + collectiblesText.width / 2, y);

    // Set depths
    heightIcon.setDepth(Z_INDEX.uiContent);
    heightText.setDepth(Z_INDEX.uiContent);
    separator.setDepth(Z_INDEX.uiContent);
    diamondIcon.setDepth(Z_INDEX.uiContent);
    collectiblesText.setDepth(Z_INDEX.uiContent);
  }

  /**
   * Request orientation permission (iOS 13+)
   */
  private async requestOrientationPermission(): Promise<void> {
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof (DeviceOrientationEvent as any).requestPermission === 'function'
    ) {
      const response = await (DeviceOrientationEvent as any).requestPermission();
      if (response !== 'granted') {
        throw new Error('Permission not granted');
      }
    }
  }

  /**
   * Launch game scene
   */
  private launchGame(levelType: LevelType): void {
    // Pass level type to GameScene via registry
    this.registry.set('selectedLevel', levelType);

    // Fade out transition
    this.cameras.main.fadeOut(300, 0, 0, 0);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GameScene');
    });
  }

  /**
   * Cleanup on scene shutdown
   */
  shutdown(): void {
    // Remove device orientation listener
    if (window.DeviceOrientationEvent) {
      window.removeEventListener('deviceorientation', this.handleDeviceOrientation.bind(this));
    }
  }
}
